/*
 * Purpose: Read-only mini Flow Map preview from plan workflowPreview
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Edge, Node } from '@/stores/workflowStore';
import { getNodeLabel } from '@/lib/workflowUtils';
import { getBlockTypeLabel } from '@/lib/blockTypes';
import {
  buildMapEdgePath,
  getMapNodeVisualStyle,
  PREVIEW_NODE_HEIGHT,
  PREVIEW_NODE_WIDTH,
} from '@/lib/mapVisuals';
import {
  layoutWorkflowNodes,
  resolveMapFlowOrientation,
  type ResolvedMapFlowOrientation,
} from '@/lib/mapFlowOrientation';
import { cn } from '@/lib/utils';

const PAD = 24;

interface ComposeFlowMapPreviewProps {
  nodes: Node[];
  edges: Edge[];
  className?: string;
}

export function ComposeFlowMapPreview({ nodes, edges, className }: ComposeFlowMapPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 640, height: 240 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setContainerSize((prev) => {
        if (prev.width === w && prev.height === h) return prev;
        return { width: w, height: h };
      });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const effectiveOrientation: ResolvedMapFlowOrientation = useMemo(
    () => resolveMapFlowOrientation('auto', containerSize.width, containerSize.height),
    [containerSize.width, containerSize.height]
  );

  const displayNodes = useMemo(() => {
    if (nodes.length === 0) return [];
    return layoutWorkflowNodes({ nodes, edges }, effectiveOrientation);
  }, [nodes, edges, effectiveOrientation]);

  const layout = useMemo(() => {
    if (displayNodes.length === 0) return null;

    const minX = Math.min(...displayNodes.map((n) => n.x)) - PAD;
    const minY = Math.min(...displayNodes.map((n) => n.y)) - PAD;
    const maxX = Math.max(...displayNodes.map((n) => n.x + PREVIEW_NODE_WIDTH)) + PAD;
    const maxY = Math.max(...displayNodes.map((n) => n.y + PREVIEW_NODE_HEIGHT)) + PAD;

    return {
      width: maxX - minX,
      height: maxY - minY,
      offsetX: -minX,
      offsetY: -minY,
    };
  }, [displayNodes]);

  if (!layout || displayNodes.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic py-4 text-center">
        Flow map preview will appear once blocks are planned.
      </p>
    );
  }

  const nodeById = new Map(displayNodes.map((n) => [n.id, n]));

  return (
    <div
      ref={containerRef}
      className={cn(
        'compose-flow-map-preview rounded-lg border border-border/60 bg-muted/20 overflow-auto',
        effectiveOrientation === 'vertical' ? 'max-h-[min(70vh,520px)]' : 'overflow-x-auto overflow-y-hidden',
        className
      )}
      aria-label="Flow map preview"
    >
      <div
        className="relative mx-auto"
        style={{ width: layout.width, height: layout.height, minWidth: '100%' }}
      >
        <svg
          className="ab-map-edges-svg absolute inset-0 pointer-events-none"
          width={layout.width}
          height={layout.height}
          aria-hidden
        >
          {edges.map((edge) => {
            const from = nodeById.get(edge.from);
            const to = nodeById.get(edge.to);
            if (!from || !to) return null;
            return (
              <path
                key={edge.id}
                className="ab-map-edge-path"
                d={buildMapEdgePath(
                  { x: from.x + layout.offsetX, y: from.y + layout.offsetY },
                  { x: to.x + layout.offsetX, y: to.y + layout.offsetY },
                  PREVIEW_NODE_WIDTH,
                  PREVIEW_NODE_HEIGHT,
                  { orientation: effectiveOrientation }
                )}
              />
            );
          })}
        </svg>

        {displayNodes.map((node) => {
          const style = getMapNodeVisualStyle(node);
          const Icon = style.icon;
          const label = getNodeLabel(node);
          const typeLabel = getBlockTypeLabel(node.type, node.subtype);

          return (
            <div
              key={node.id}
              className={cn(
                'ab-map-glass-node absolute flex items-center gap-2 rounded-lg border px-2 py-1.5',
                style.bg,
                style.border
              )}
              style={{
                left: node.x + layout.offsetX,
                top: node.y + layout.offsetY,
                width: PREVIEW_NODE_WIDTH,
                height: PREVIEW_NODE_HEIGHT,
              }}
            >
              <Icon className={cn('h-3.5 w-3.5 shrink-0', style.iconClass)} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold leading-tight truncate">{label}</p>
                <p className="text-[9px] text-muted-foreground truncate">{typeLabel}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
